<template>
  <div class="kadmin-page">
    <div class="kadmin-topbar">
      <span class="kadmin-brand">
        Manutenzione fondo cassa
        <span v-if="staffName" class="kadmin-operator">· {{ staffName }}</span>
      </span>
      <!-- Niente uscita mentre il device sta erogando (si perderebbe il totem sulla home pubblica). -->
      <button v-if="screen !== 'processing'" class="kadmin-exit" @click="exit">Esci ✕</button>
    </div>

    <!-- Errore NON bloccante di lettura livelli: banner, non nasconde il pannello. -->
    <div v-if="levelError" class="kadmin-banner">
      {{ levelError }}
      <button class="kadmin-banner__retry" :disabled="loading" @click="loadLevels">Riprova</button>
    </div>

    <!-- HOME: riepilogo fondo cassa + azioni -->
    <template v-if="screen === 'home'">
      <div class="kadmin-total">
        <span class="kadmin-total__label">Fondo cassa totale</span>
        <span class="kadmin-total__value">{{ fmt(levels.total) }}</span>
        <button class="kadmin-refresh" :disabled="loading" @click="loadLevels">{{ loading ? 'Aggiorno…' : '↻ Aggiorna' }}</button>
      </div>

      <div class="kadmin-cols">
        <div class="kadmin-card">
          <h4>Banconote</h4>
          <ul>
            <li v-for="d in notes" :key="'n'+d.value">
              <span>{{ fmt(d.value) }}</span>
              <span class="kadmin-x">× {{ d.level }}</span>
              <span class="kadmin-sub">{{ fmt(d.value * d.level) }}</span>
            </li>
            <li v-if="!notes.length" class="kadmin-empty">Nessuna banconota</li>
          </ul>
        </div>
        <div class="kadmin-card">
          <h4>Monete</h4>
          <ul>
            <li v-for="d in coins" :key="'c'+d.value">
              <span>{{ fmt(d.value) }}</span>
              <span class="kadmin-x">× {{ d.level }}</span>
              <span class="kadmin-sub">{{ fmt(d.value * d.level) }}</span>
            </li>
            <li v-if="!coins.length" class="kadmin-empty">Nessuna moneta</li>
          </ul>
        </div>
      </div>

      <div class="kadmin-actions">
        <button class="kadmin-btn kadmin-btn--primary" @click="goWithdraw">Preleva un importo</button>
        <button class="kadmin-btn" @click="startRefill">Carica fondo cassa</button>
        <button class="kadmin-btn kadmin-btn--danger" @click="screen = 'confirmEmpty'">Svuota / preleva tutto</button>
        <button class="kadmin-btn kadmin-btn--ghost" @click="screen = 'confirmQuit'">Chiudi applicazione</button>
      </div>
      <p class="kadmin-note">Il <b>carico</b> è un rifornimento (float): non viene registrato ai fini fiscali.</p>
    </template>

    <!-- PRELIEVO: inserimento importo -->
    <template v-else-if="screen === 'withdraw'">
      <h3 class="kadmin-h">Preleva un importo</h3>
      <div class="kadmin-amount">{{ fmt(amountCents) }}</div>
      <p class="kadmin-hint">Disponibile: {{ fmt(levels.total) }}</p>
      <p v-if="amountCents > levels.total" class="kadmin-hint kadmin-hint--warn">L'importo supera il fondo cassa disponibile.</p>
      <div class="kadmin-pad">
        <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n" class="kadmin-key" @click="padPress(n)">{{ n }}</button>
        <button class="kadmin-key kadmin-key--ghost" @click="padClear">C</button>
        <button class="kadmin-key" @click="padPress(0)">0</button>
        <button class="kadmin-key kadmin-key--ghost" @click="padBackspace">⌫</button>
      </div>
      <div class="kadmin-row">
        <button class="kadmin-btn kadmin-btn--ghost" @click="backHome">Annulla</button>
        <button class="kadmin-btn kadmin-btn--primary" :disabled="amountCents <= 0 || amountCents > levels.total" @click="confirmWithdraw">Eroga {{ fmt(amountCents) }}</button>
      </div>
    </template>

    <!-- CONFERMA SVUOTAMENTO -->
    <template v-else-if="screen === 'confirmEmpty'">
      <h3 class="kadmin-h">Svuota / preleva tutto</h3>
      <p class="kadmin-hint">Verranno erogate <b>tutte</b> le banconote e le monete presenti ({{ fmt(levels.total) }}). Confermi?</p>
      <div class="kadmin-row">
        <button class="kadmin-btn kadmin-btn--ghost" @click="backHome">Annulla</button>
        <button class="kadmin-btn kadmin-btn--danger" @click="confirmEmptyAll">Sì, svuota tutto</button>
      </div>
    </template>

    <!-- CONFERMA CHIUSURA APP -->
    <template v-else-if="screen === 'confirmQuit'">
      <h3 class="kadmin-h">Chiudi applicazione</h3>
      <p class="kadmin-hint">L'app del kiosk verrà chiusa e comparirà il desktop di Windows. Per riaprirla riavvia il kiosk (si riavvia da sola) o rilanciala. Confermi?</p>
      <div class="kadmin-row">
        <button class="kadmin-btn kadmin-btn--ghost" @click="backHome">Annulla</button>
        <button class="kadmin-btn kadmin-btn--danger" @click="quitApp">Sì, chiudi</button>
      </div>
    </template>

    <!-- OPERAZIONE IN CORSO (prelievo / svuota) -->
    <template v-else-if="screen === 'processing'">
      <div class="kadmin-spinner"></div>
      <h3 class="kadmin-h">{{ processingTitle }}</h3>
      <p class="kadmin-hint" v-if="dispensedLive != null">Erogato finora: {{ fmt(dispensedLive) }}</p>
      <p class="kadmin-hint">Attendere il completamento…</p>
    </template>

    <!-- CARICO (refill) -->
    <template v-else-if="screen === 'refill'">
      <h3 class="kadmin-h">Carico fondo cassa</h3>
      <p class="kadmin-hint">Inserisci monete e banconote nel dispositivo.</p>
      <div class="kadmin-amount kadmin-amount--in">+ {{ fmt(refillInserted) }}</div>
      <p class="kadmin-hint">Caricato in questa sessione (non fiscale)</p>
      <div class="kadmin-row">
        <button class="kadmin-btn kadmin-btn--primary" :disabled="refillStopping" @click="stopRefill">{{ refillStopping ? 'Chiudo…' : 'Termina carico' }}</button>
      </div>
    </template>

    <!-- ESITO -->
    <template v-else-if="screen === 'result'">
      <div class="kadmin-badge" :class="result.warn ? 'kadmin-badge--warn' : 'kadmin-badge--ok'">
        <span v-if="result.warn">!</span><span v-else>✓</span>
      </div>
      <h3 class="kadmin-h">{{ result.title }}</h3>
      <p v-for="(line, i) in result.lines" :key="i" class="kadmin-hint">{{ line }}</p>
      <div class="kadmin-row">
        <button class="kadmin-btn kadmin-btn--primary" @click="backHome">Torna al pannello</button>
      </div>
    </template>
  </div>
</template>

<script>
import { IS_KIOSK } from '@/config/kiosk'

const UNLOCK_MAX_AGE_MS = 5 * 60 * 1000
const REFILL_POLL_MS = 900
const REFILL_MAX_MS = 10 * 60 * 1000
// Re-lock del pannello dopo inattività (solo su schermate "ferme": mai durante
// un'erogazione o un carico in corso, per non interromperli).
const INACTIVITY_MS = 3 * 60 * 1000

export default {
  name: 'KioskAdminPage',
  data () {
    return {
      token: '',
      staffName: '',
      loading: false,
      levelError: '',   // errore NON bloccante di lettura livelli (banner)
      levels: { total: 0, denominations: [] },
      screen: 'home',
      amountCents: 0,
      processingTitle: '',
      dispensedLive: null,
      refillInserted: 0,
      refillStopping: false,
      result: { title: '', lines: [], warn: false },
      adminUnsub: null,
      refillTimer: null,
      refillStartedAt: 0,
      refillActive: false,
      inactivityTimer: null,
    }
  },
  computed: {
    notes () {
      return (this.levels.denominations || []).filter((d) => d.type === 'note').sort((a, b) => b.value - a.value)
    },
    coins () {
      return (this.levels.denominations || []).filter((d) => d.type !== 'note').sort((a, b) => b.value - a.value)
    },
  },
  watch: {
    // A ogni cambio schermata riprogramma il re-lock (disattivo su op in corso).
    screen () { this.resetInactivity() },
  },
  mounted () {
    // Guardia: raggiungibile solo dopo lo sblocco (gate) e con un token valido.
    let okUnlock = false
    try {
      const ts = Number(sessionStorage.getItem('kioskAdminUnlockedAt') || 0)
      this.token = sessionStorage.getItem('kioskAdminToken') || ''
      this.staffName = sessionStorage.getItem('kioskAdminStaff') || ''
      okUnlock = ts > 0 && (Date.now() - ts) < UNLOCK_MAX_AGE_MS
    } catch (e) { okUnlock = false }
    if (!IS_KIOSK || !window.kioskPay || !okUnlock || !this.token) {
      this.$router.replace({ path: '/kiosk' })
      return
    }
    this._onActivity = () => this.resetInactivity()
    window.addEventListener('pointerdown', this._onActivity, { passive: true })
    window.addEventListener('keydown', this._onActivity, { passive: true })
    this.resetInactivity()
    this.loadLevels()
  },
  beforeUnmount () {
    this.clearRefillPoll()
    if (this.inactivityTimer) { clearTimeout(this.inactivityTimer); this.inactivityTimer = null }
    if (this._onActivity) {
      window.removeEventListener('pointerdown', this._onActivity)
      window.removeEventListener('keydown', this._onActivity)
    }
    if (this.adminUnsub) { try { this.adminUnsub() } catch (e) { /* noop */ } }
    // Sicurezza: se si esce con un carico ancora aperto, chiudilo sul device.
    if (this.refillActive && window.kioskPay && window.kioskPay.refillStop) {
      try { window.kioskPay.refillStop({ token: this.token }) } catch (e) { /* noop */ }
      this.refillActive = false
    }
    // Chiude la sessione admin: invalida il token nel main e in sessionStorage.
    try { if (window.kioskPay && window.kioskPay.adminLogout) window.kioskPay.adminLogout() } catch (e) { /* noop */ }
    try {
      sessionStorage.removeItem('kioskAdminToken')
      sessionStorage.removeItem('kioskAdminUnlockedAt')
      sessionStorage.removeItem('kioskAdminStaff')
    } catch (e) { /* noop */ }
  },
  methods: {
    fmt (cents) {
      try {
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(cents || 0) / 100)
      } catch (e) {
        return `${(Number(cents || 0) / 100).toFixed(2)} €`
      }
    },
    // Re-lock: nessun timer durante un'erogazione/carico (non vanno interrotti).
    resetInactivity () {
      if (this.inactivityTimer) { clearTimeout(this.inactivityTimer); this.inactivityTimer = null }
      if (this.screen === 'processing' || this.screen === 'refill') return
      this.inactivityTimer = setTimeout(() => this.exit(), INACTIVITY_MS)
    },
    // Ritorna true (e forza il logout) se la risposta è "non autorizzato".
    handleUnauthorized (res) {
      if (res && res.unauthorized) {
        this.exit()
        return true
      }
      return false
    },
    async loadLevels () {
      this.loading = true
      this.levelError = ''
      try {
        const res = await window.kioskPay.getCashLevels({ token: this.token })
        if (this.handleUnauthorized(res)) return
        if (res && res.ok === false) throw new Error(res.error || 'Errore lettura livelli')
        this.levels = { total: Number(res.total || 0), denominations: res.denominations || [] }
      } catch (e) {
        this.levelError = 'Impossibile leggere il fondo cassa: ' + (e.message || e)
      } finally {
        this.loading = false
      }
    },
    backHome () {
      this.screen = 'home'
      this.amountCents = 0
      this.loadLevels()
    },
    goWithdraw () {
      this.amountCents = 0
      this.screen = 'withdraw'
    },
    padPress (n) {
      const next = this.amountCents * 10 + n
      if (next > 100000000) return // limite €1.000.000
      this.amountCents = next
    },
    padBackspace () { this.amountCents = Math.floor(this.amountCents / 10) },
    padClear () { this.amountCents = 0 },

    subscribeProgress (onDispensed) {
      if (this.adminUnsub) { try { this.adminUnsub() } catch (e) { /* noop */ } }
      this.adminUnsub = window.kioskPay.onAdminProgress((p) => {
        if (p && typeof p.dispensed === 'number') onDispensed(p.dispensed)
      })
    },
    async confirmWithdraw () {
      if (this.amountCents <= 0 || this.amountCents > this.levels.total) return
      const amount = this.amountCents
      this.processingTitle = `Erogazione di ${this.fmt(amount)}…`
      this.dispensedLive = 0
      this.screen = 'processing'
      this.subscribeProgress((d) => { this.dispensedLive = d })
      try {
        const res = await window.kioskPay.withdraw({ amount, token: this.token })
        if (this.handleUnauthorized(res)) return
        this.showDispenseResult('Prelievo', amount, res)
      } catch (e) {
        this.showError('Errore durante il prelievo', e)
      } finally {
        if (this.adminUnsub) { try { this.adminUnsub() } catch (er) { /* noop */ } this.adminUnsub = null }
      }
    },
    async confirmEmptyAll () {
      this.processingTitle = 'Svuotamento in corso…'
      this.dispensedLive = 0
      this.screen = 'processing'
      this.subscribeProgress((d) => { this.dispensedLive = d })
      try {
        const res = await window.kioskPay.emptyAll({ token: this.token })
        if (this.handleUnauthorized(res)) return
        this.showDispenseResult('Svuotamento', null, res)
      } catch (e) {
        this.showError('Errore durante lo svuotamento', e)
      } finally {
        if (this.adminUnsub) { try { this.adminUnsub() } catch (er) { /* noop */ } this.adminUnsub = null }
      }
    },
    showDispenseResult (label, requested, res) {
      if (res && res.ok === false && res.error) {
        this.showError(`${label} non riuscito`, new Error(res.error))
        return
      }
      const dispensed = Number((res && res.dispensed) || 0)
      const notDispensed = Number((res && res.notDispensed) || 0)
      const ok = !!(res && res.ok) && notDispensed === 0
      const lines = [`Erogato: ${this.fmt(dispensed)}`]
      if (requested != null && requested !== dispensed) lines.unshift(`Richiesto: ${this.fmt(requested)}`)
      if (notDispensed > 0) lines.push(`Non erogabile: ${this.fmt(notDispensed)} (tagli non disponibili).`)
      this.result = { title: ok ? `${label} completato` : `${label} parziale`, lines, warn: !ok }
      this.screen = 'result'
    },
    showError (title, e) {
      this.result = { title, lines: [String((e && e.message) || e)], warn: true }
      this.screen = 'result'
    },

    async startRefill () {
      try {
        const res = await window.kioskPay.refillStart({ token: this.token })
        if (this.handleUnauthorized(res)) return
        if (res && res.ok === false) throw new Error(res.error || 'Avvio carico fallito')
        this.refillActive = true
        this.refillInserted = 0
        this.refillStopping = false
        this.screen = 'refill'
        this.refillStartedAt = Date.now()
        this.clearRefillPoll()
        this.refillTimer = setInterval(this.pollRefill, REFILL_POLL_MS)
      } catch (e) {
        this.showError('Impossibile avviare il carico', e)
      }
    },
    async pollRefill () {
      // Chiusura di sicurezza se la sessione resta aperta troppo a lungo.
      if (Date.now() - this.refillStartedAt > REFILL_MAX_MS) {
        this.stopRefill()
        return
      }
      try {
        const st = await window.kioskPay.refillStatus({ token: this.token })
        if (st && st.unauthorized) { this.stopRefill(); return }
        if (st && typeof st.inserted === 'number') this.refillInserted = st.inserted
      } catch (e) { /* transitorio: riprova al prossimo tick */ }
    },
    async stopRefill () {
      if (this.refillStopping) return
      this.refillStopping = true
      this.clearRefillPoll()
      try {
        const res = await window.kioskPay.refillStop({ token: this.token })
        this.refillActive = false
        if (res && res.levels) this.levels = { total: Number(res.levels.total || 0), denominations: res.levels.denominations || [] }
        this.result = {
          title: 'Carico completato',
          lines: [`Caricato: ${this.fmt(this.refillInserted)} (non fiscale)`, `Nuovo fondo cassa: ${this.fmt(this.levels.total)}`],
          warn: false,
        }
        this.screen = 'result'
      } catch (e) {
        this.refillActive = false
        this.showError('Errore nella chiusura del carico', e)
      } finally {
        this.refillStopping = false
      }
    },
    clearRefillPoll () {
      if (this.refillTimer) { clearInterval(this.refillTimer); this.refillTimer = null }
    },

    exit () {
      if (this._exiting) return
      this._exiting = true
      // Logout backend + eventuale chiusura del refill avvengono in beforeUnmount
      // (fire-and-forget): NON blocchiamo l'uscita sull'attesa di rete.
      try {
        sessionStorage.removeItem('kioskAdminToken')
        sessionStorage.removeItem('kioskAdminUnlockedAt')
        sessionStorage.removeItem('kioskAdminStaff')
      } catch (e) { /* noop */ }
      this.$router.push({ path: '/kiosk' })
    },

    async quitApp () {
      try {
        if (window.kioskPay && window.kioskPay.quitApp) {
          await window.kioskPay.quitApp({ token: this.token })
        }
      } catch (e) { /* noop */ }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.kadmin-page {
  min-height: 100vh;
  background: #292D7D;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  padding: 20px 20px 40px;
  box-sizing: border-box;
  text-align: center;
}
.kadmin-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.kadmin-brand { font-weight: 700; font-size: 1.2rem; }
.kadmin-operator { font-weight: 500; font-size: 0.95rem; opacity: 0.8; }
.kadmin-exit {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.4);
  color: #fff;
  border-radius: 10px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
}
.kadmin-fatal { margin-top: 40px; color: #ff8a8a; font-weight: 600; }

.kadmin-total {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 18px;
  padding: 20px;
  margin-bottom: 18px;
}
.kadmin-total__label { opacity: 0.8; font-size: 1rem; }
.kadmin-total__value { font-size: 2.6rem; font-weight: 700; color: #ffc933; }
.kadmin-refresh {
  margin-top: 6px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.4);
  color: #fff;
  border-radius: 10px;
  padding: 6px 14px;
  cursor: pointer;
}

.kadmin-cols { display: flex; gap: 14px; margin-bottom: 20px; }
.kadmin-card {
  flex: 1;
  background: rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 16px;
  text-align: left;
}
.kadmin-card h4 { margin: 0 0 10px; font-size: 1.05rem; }
.kadmin-card ul { list-style: none; margin: 0; padding: 0; }
.kadmin-card li { display: flex; align-items: center; padding: 6px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
.kadmin-card li span:first-child { width: 70px; font-weight: 600; }
.kadmin-x { flex: 1; opacity: 0.75; }
.kadmin-sub { font-weight: 600; }
.kadmin-empty { opacity: 0.6; }

.kadmin-actions { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
.kadmin-actions .kadmin-btn { flex: 1 1 220px; max-width: 340px; min-height: 64px; }
.kadmin-note { opacity: 0.7; font-size: 0.9rem; margin-top: 14px; }

.kadmin-btn {
  padding: 16px 20px;
  font-size: 1.15rem;
  font-weight: 600;
  border: none;
  border-radius: 14px;
  background: rgba(255,255,255,0.12);
  color: #fff;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
}
.kadmin-btn:active { filter: brightness(0.94); }
.kadmin-btn[disabled] { opacity: 0.5; }
.kadmin-btn--primary { background: #ffc933; color: #1a1a1a; }
.kadmin-btn--danger { background: #e74c3c; }
.kadmin-btn--ghost { background: transparent; border: 1px solid rgba(255,255,255,0.4); }

.kadmin-h { font-size: 1.5rem; font-weight: 700; margin: 8px 0 6px; }
.kadmin-hint { opacity: 0.85; font-size: 1.05rem; margin: 4px 0; }
.kadmin-amount { font-size: 3rem; font-weight: 700; color: #ffc933; margin: 10px 0; }
.kadmin-amount--in { color: #2ecc71; }

.kadmin-pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  max-width: 380px;
  margin: 16px auto;
}
.kadmin-key {
  padding: 18px 0;
  font-size: 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: 14px;
  background: rgba(255,255,255,0.1);
  color: #fff;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
}
.kadmin-key:active { background: rgba(255,255,255,0.24); }
.kadmin-key--ghost { background: transparent; border: 1px solid rgba(255,255,255,0.35); font-size: 1.2rem; }

.kadmin-row { display: flex; gap: 12px; justify-content: center; max-width: 460px; margin: 20px auto 0; }
.kadmin-row .kadmin-btn { flex: 1; }

.kadmin-spinner {
  width: 72px; height: 72px; margin: 20px auto 0;
  border-radius: 50%;
  border: 7px solid rgba(255,255,255,0.18);
  border-top-color: #ffc933;
  animation: kadmin-spin 0.9s linear infinite;
}
@keyframes kadmin-spin { to { transform: rotate(360deg); } }

.kadmin-badge {
  width: 90px; height: 90px; margin: 24px auto 8px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 3rem; font-weight: 700; color: #fff;
}
.kadmin-badge--ok { background: #2ecc71; }
.kadmin-badge--warn { background: #e67e22; }
</style>
